import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

async function loadProfile(supabaseUser) {
  const { data: profile } = await supabase
    .from('profiles')
    .select('display_name, is_admin, created_at')
    .eq('id', supabaseUser.id)
    .maybeSingle();

  return {
    id: supabaseUser.id,
    email: supabaseUser.email,
    name: profile?.display_name || supabaseUser.user_metadata?.display_name || supabaseUser.email.split('@')[0],
    is_admin: profile?.is_admin ?? false,
    created_at: profile?.created_at ?? supabaseUser.created_at,
  };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  const applySession = useCallback(async (nextSession) => {
    setSession(nextSession);
    if (nextSession?.user) {
      setUser(await loadProfile(nextSession.user));
    } else {
      setUser(null);
    }
    // Nudge the shared embed header (www.doctorgemma.com/embed/header.js) to
    // re-read the session cookie so its Sign in / Log out buttons update
    // immediately after an in-app login or logout, without a page reload.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('dg-auth-changed'));
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      applySession(data.session).finally(() => setLoading(false));
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      applySession(nextSession);
    });

    return () => subscription.subscription.unsubscribe();
  }, [applySession]);

  const signup = async (email, password, name) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { display_name: name } },
    });

    if (error) {
      toast.error(error.message || 'Signup failed');
      return false;
    }

    if (!data.session) {
      toast.success('Account created! Check your email to confirm before signing in.');
      return false;
    }

    toast.success('Account created successfully!');
    return true;
  };

  const login = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      toast.error(error.message || 'Login failed');
      return false;
    }
    toast.success('Welcome back!');
    return true;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    toast.success('Logged out successfully');
  };

  const forgotPassword = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) {
      toast.error(error.message || 'Failed to send reset email');
      return false;
    }
    return true;
  };

  const refreshUser = async () => {
    if (session?.user) setUser(await loadProfile(session.user));
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      signup,
      login,
      logout,
      forgotPassword,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
